const PHOTO_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const PHOTO_EXTENSIONS = new Map([['jpg', 'image/jpeg'], ['jpeg', 'image/jpeg'], ['png', 'image/png'], ['webp', 'image/webp']]);
// Memorial and Kings & Queens take Photo 1-3. A form can allow more slots with data-photo-slots
// (Custom: 3 per pet for up to 3 pets, plus Photo 10, the "make it like this" picture).
const photoInputNames = form => new Set(Array.from({length: Number(form?.dataset?.photoSlots) || 3}, (_, i) => 'properties[_Photo ' + (i + 1) + ']'));
const PHOTO_COUNT_ERROR = 'Add up to 3 photos. Please remove any extra photos.';
const photoLabel = file => file.name ? '“' + file.name + '”' : 'This photo';
// "We should be able to ingest anything" (Casey, 1 Nov): any picture goes through (HEIC, AVIF, GIF, no file ending, no type);
// the browser turns what it can into a JPEG first and Rover converts the rest. Only things that aren't pictures are stopped.
const NOT_PICTURE_EXTENSIONS = new Set(['pdf', 'mp4', 'mov', 'm4v', 'avi', 'webm', 'mkv', '3gp', 'doc', 'docx', 'txt', 'zip']);
export const isNotPicture = file => /^(video|audio|text)\//.test(file.type || '') || /^application\/(pdf|zip|msword)/.test(file.type || '')
  || NOT_PICTURE_EXTENSIONS.has(String(file.name?.match(/\.([a-z0-9]+)$/i)?.[1] || '').toLowerCase());
const photoFormatError = () => 'That’s not a photo. Please choose a photo.';
// Checked after the browser has shrunk what it can, so only a photo it can't read gets near this; Rover takes 30 MB a photo, 32 MB a form (batch 6).
export const DRAFT_MAX_PHOTO_BYTES = 25_000_000;
export const DRAFT_MAX_PHOTOS = 3;
export function validatePhotos(files, maxPhotos = DRAFT_MAX_PHOTOS) {
  if (!files.length) return 'Choose your favourite photo before continuing.';
  if (files.length > maxPhotos) return maxPhotos === DRAFT_MAX_PHOTOS ? PHOTO_COUNT_ERROR : 'Too many photos. Please remove any extra photos.';
  for (const file of files) {
    if (file.size > DRAFT_MAX_PHOTO_BYTES) return photoLabel(file) + ' is larger than 25 MB. Please choose another photo.';
    if (isNotPicture(file)) return photoFormatError(file);
    if (file.size < 1) return photoLabel(file) + ' could not be opened. Please choose another photo.';
  }
  return '';
}
const FTYP_PICTURE = ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1', 'msf1', 'avif', 'avis'];
// True when a file's first bytes say PDF or video: %PDF, Matroska/WebM (1A45DFA3), RIFF…AVI, or an ftyp box (MP4, MOV) that
// lists no picture brand. HEIC and AVIF list one, so they pass.
export async function notPictureInside(file) {
  let b;
  try { b = new Uint8Array(await file.slice(0, 256).arrayBuffer()); } catch { return false; }
  const text = (from, to) => String.fromCharCode(...b.subarray(from, to));
  if (text(0, 4) === '%PDF') return true;
  if (b[0] === 0x1a && b[1] === 0x45 && b[2] === 0xdf && b[3] === 0xa3) return true;
  if (text(0, 4) === 'RIFF' && text(8, 12) === 'AVI ') return true;
  if (text(4, 8) === 'ftyp') {
    const size = ((b[0] << 24) >>> 0) + (b[1] << 16) + (b[2] << 8) + b[3], end = Math.min(b.length, Math.max(16, size), 256), brands = [text(8, 12)];
    for (let at = 16; at + 4 <= end; at += 4) brands.push(text(at, at + 4));
    return !brands.some(x => FTYP_PICTURE.includes(x));
  }
  return false;
}
export async function validatePhotoContents(files) {
  for (const file of files) {
    let bytes;
    try { bytes = new Uint8Array(await file.slice(0, 12).arrayBuffer()); }
    catch { return photoLabel(file) + ' could not be opened. Please choose another photo.'; }
    const actualType = bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff ? 'image/jpeg'
      : [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every((byte, index) => bytes[index] === byte) ? 'image/png'
      : bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46
        && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50 ? 'image/webp' : '';
    // A picture that isn't a JPEG, PNG or WebP inside (HEIC named .jpg, AVIF…) still goes: Rover converts it. A non-picture is
    // stopped, by its name or by its first bytes, the same list as Rover's photoKind (Big Guns, 1 Nov: a PDF renamed .jpg must
    // never reach the paid cart).
    if (!actualType && (isNotPicture(file) || await notPictureInside(file))) return photoFormatError(file);
  }
  return '';
}
// Big photos (Snapseed and other editors save full-size files of 15-25 MB on new phones) are shrunk in the
// browser before upload: long side at most 3000 px, a JPEG well under the limit. Small, ordinary photos are
// left exactly as they are. If the browser can't read a photo it stays as chosen and the usual checks explain.
export const SHRINK_OVER_BYTES = 4_000_000;
const PHOTO_MAX_SIDE = 3000;
// Everything sent at once stays under Rover's 32 MiB form (batch 6; it was 16 MB when Leanne's ordinary 3-4 MB photos added up
// to more than Rover took on 24 Oct). A raw photo the browser can't shrink still counts, so the budget leaves room for one
// (Basic Guns, 1 Nov: an 11 MB budget crushed everyone's normal photos to 1400 px whenever a raw one was there).
export const PHOTO_BUDGET_BYTES = 28_000_000;
// The most one free-preview form may carry: just under Rover's 32 MiB.
export const FORM_MAX_BYTES = 31_500_000;
// Shopify's cart takes files up to 20 MB, so a picture the browser can't read may still go up to that.
export const CART_RAW_MAX_BYTES = 20_000_000;
export async function shrinkPhoto(file, win, {force = false, side = PHOTO_MAX_SIDE, quality = 0.92} = {}) {
  const extension = file.name?.match(/\.([a-z0-9]+)$/i)?.[1].toLowerCase();
  const ordinary = PHOTO_TYPES.has(file.type) && PHOTO_EXTENSIONS.get(extension) === file.type;
  if (!force && ordinary && file.size <= SHRINK_OVER_BYTES) return file;
  try {
    let source;
    try { source = await win.createImageBitmap(file, {imageOrientation: 'from-image'}); }
    catch {
      source = await new Promise((resolve, reject) => {
        const image = new win.Image(), url = win.URL.createObjectURL(file);
        image.onload = () => { win.URL.revokeObjectURL(url); resolve(image); };
        image.onerror = () => { win.URL.revokeObjectURL(url); reject(new Error('unreadable')); };
        image.src = url;
      });
    }
    const width = source.width || source.naturalWidth, height = source.height || source.naturalHeight;
    if (!width || !height) return file;
    const scale = Math.min(1, side / Math.max(width, height));
    const canvas = win.document.createElement('canvas');
    canvas.width = Math.round(width * scale); canvas.height = Math.round(height * scale);
    const context = canvas.getContext('2d');
    context.fillStyle = '#fff'; context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(source, 0, 0, canvas.width, canvas.height);
    source.close?.();
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob || !blob.size || (ordinary && blob.size >= file.size)) return file;
    return new win.File([blob], (file.name || 'photo').replace(/\.[a-z0-9]+$/i, '') + '.jpg', {type: 'image/jpeg', lastModified: Date.now()});
  } catch { return file; }
}
// Pass 0 shrinks only the big photos. If they still add up to more than the budget, each later pass shrinks every
// photo harder (harder = 1 starts there, e.g. after a send that failed).
export const SHRINK_PASSES = [{}, {force: true, side: 2400, quality: 0.85}, {force: true, side: 1800, quality: 0.8}, {force: true, side: 1400, quality: 0.75}];
async function shrinkPhotoInputs(form, win, harder = 0) {
  const inputs = () => [...form.querySelectorAll('[data-photo-input]')].filter(input => input.files?.[0] && !input.disabled);
  const total = () => inputs().reduce((sum, input) => sum + input.files[0].size, 0);
  for (let pass = harder; pass < SHRINK_PASSES.length; pass++) {
    if (pass > harder && total() <= PHOTO_BUDGET_BYTES) break;
    for (const input of inputs()) {
      const file = input.files[0], smaller = await shrinkPhoto(file, win, SHRINK_PASSES[pass]);
      if (smaller === file || !win.DataTransfer) continue;
      const transfer = new win.DataTransfer(); transfer.items.add(smaller); input.files = transfer.files;
    }
    if (pass === 0 && total() <= PHOTO_BUDGET_BYTES) break;
  }
}
// Duplicate protection (Casey, 18 Oct): the pets a portrait is of, from the form or from a cart line.
const petNames = get => { const one = get('Companion name'); const many = [1, 2, 3].map(n => get('Pet ' + n + ' name')).filter(Boolean); return (many.length ? many : one ? [one] : []).map(x => String(x).trim().toLowerCase()).filter(Boolean).sort().join('+'); };
export function sameInCart(cart, handle, data) {
  const mine = petNames(key => data.get('properties[' + key + ']'));
  if (!mine) return null;
  return (cart?.items || []).find(item => item.handle === handle && petNames(key => item.properties?.[key]) === mine) || null;
}
export function isSavedPhotoURL(value, origin) {
  if (typeof value !== 'string' || !value.trim()) return false;
  try {
    const url = new URL(value, origin);
    const allowedLocation = value.startsWith('/uploads/') || ((value.startsWith('https://') || value.startsWith('//')) && url.hostname === 'cdn.shopify.com');
    return url.protocol === 'https:' && allowedLocation && url.pathname.includes('/uploads/');
  } catch { return false; }
}
export function verifyPortraitInCart(cart, {reference, variantId, photoKeys, origin}) {
  const matches = (cart?.items || []).filter(item => item.properties?.['_Dear Friend reference'] === reference);
  if (matches.length !== 1) return null;
  const item = matches[0];
  if (String(item.variant_id ?? item.id) !== String(variantId) || item.quantity !== 1) return null;
  if (item.properties?.['_Photo count'] !== String(photoKeys.length)) return null;
  if (Object.keys(item.properties || {}).some(key => /^_?Photo \d+$/.test(key) && !photoKeys.includes(key))) return null;
  return photoKeys.every(key => isSavedPhotoURL(item.properties?.[key], origin)) ? item : null;
}
export function preparePortraitPayload(form, FormDataType, reference) {
  const names = photoInputNames(form);
  const allPhotoInputs = [...form.querySelectorAll('[data-photo-input]')].filter(input => !input.disabled);
  if (allPhotoInputs.some(input => (!names.has(input.name) && input.files?.length) || input.files?.length > 1)) return {error: PHOTO_COUNT_ERROR};
  const photoInputs = allPhotoInputs.filter(input => names.has(input.name));
  if (new Set(photoInputs.map(input => input.name)).size !== photoInputs.length) return {error: PHOTO_COUNT_ERROR};
  const files = photoInputs.flatMap(input => [...(input.files || [])]);
  const error = validatePhotos(files, names.size);
  if (error) return {error};
  if (!photoInputs.some(input => input.name === 'properties[_Photo 1]' && input.files?.length)) return {error: 'Choose your favourite photo before continuing.'};
  // A form can mark other photos as needed too (Custom: each pet's first photo), with its own message.
  const missing = photoInputs.find(input => input.dataset.photoRequired && !input.files?.length);
  if (missing) return {error: missing.dataset.photoRequired};
  const data = new FormDataType(form);
  for (const name of [...data.keys()]) if (/^properties\[_?Photo \d+\]$/.test(name)) data.delete(name);
  const photoKeys = [];
  for (const input of photoInputs) {
    data.delete(input.name);
    if (!input.files?.length) continue;
    const file = input.files[0];
    data.set(input.name, file);
    photoKeys.push(input.name.slice('properties['.length, -1));
  }
  data.set('quantity', '1');
  data.set('properties[_Dear Friend reference]', reference);
  data.set('properties[_Photo count]', String(files.length));
  if (String(data.get('properties[Gift recipient]') || '').trim() || String(data.get('properties[Gift message]') || '').trim()) data.set('properties[Gift]', 'Yes');
  else data.delete('properties[Gift]');
  return {data, photoKeys, files, variantId: data.get('id')};
}
// The size line for the chosen finish (Casey, 21 Oct): framed (glazing and image area), a canvas, or the digital file.
export function describeFramedSize(size, orientation) {
  const dimensions = (pair, places = 2) => {
    const values = orientation === 'Landscape' ? [...pair].reverse() : pair;
    return values.join(' × ') + ' in (' + values.map(v=>Number((v*2.54).toFixed(places))).join(' × ') + ' cm)';
  };
  // size: "canvas-16x20" or "framed-12x16" from the finish picker (the sizes come from the Shopify variants); a frame's
  // picture is 4 in smaller each way (Prodigi's 2 in mount). "large"/"medium": products without the finish picker.
  const m = /^(framed|canvas)-(\d{1,2})x(\d{1,2})$/.exec(String(size || ''));
  if (m && m[1] === 'canvas') return 'Canvas ' + dimensions([+m[2], +m[3]], 1) + ', 38 mm deep, gallery-wrapped. The picture’s edge is mirrored round the sides.';
  if (size === 'digital') return 'The full artwork as a high-resolution file, emailed once you approve your portrait.';
  const specs = {medium:{glazing:[12,16],image:[8,12]},large:{glazing:[16,20],image:[12,16]}};
  const spec = m ? {glazing:[+m[2], +m[3]], image:[+m[2] - 4, +m[3] - 4]} : specs[size]; if (!spec) return 'See the product description for dimensions.';
  return 'Glazing ' + dimensions(spec.glazing) + ' · nominal image area ' + dimensions(spec.image) + '. Not the outer frame size.';
}
export function mountPurchase({document, window, URL, fetch, decodePhoto, requestTimeoutMs = 60000}) {
  const form = document.getElementById('DearFriendProductForm');
  if (!form) return null;
  const button = form.querySelector('#df-add-portrait'), error = form.querySelector('#df-order-error');
  const status = form.querySelector('#df-order-status'), checkCart = form.querySelector('#df-check-cart');
  const preview = form.querySelector('#df-photo-previews'), selector = form.querySelector('[name="id"]');
  const listeners = [], urls = []; let submitting = false, uncertain = false, completed = false, destroyed = false;
  const disabledBefore = new Map();
  const freeze = active => {
    if(active) for(const field of form.elements) {disabledBefore.set(field,field.disabled);field.disabled=true;}
    else {for(const [field,disabled] of disabledBefore)field.disabled=disabled;disabledBefore.clear();}
  };
  const reference = window.crypto.randomUUID();
  const requestJSON = async (url, options, timeoutMs = requestTimeoutMs) => {
    const controller = new window.AbortController();
    let timer;
    try {
      return await Promise.race([
        (async () => {
          const response = await fetch(url, {...options, signal: controller.signal});
          const result = await response.json();
          return {response, result};
        })(),
        new Promise((resolve, reject) => {
          timer = window.setTimeout(() => {
            controller.abort();
            reject(new Error('Request timed out; the cart may have changed'));
          }, timeoutMs);
        })
      ]);
    } finally { window.clearTimeout(timer); }
  };
  const readablePhoto = decodePhoto || (file => new Promise(resolve => {
    const image = new window.Image(), url = URL.createObjectURL(file);
    let settled = false;
    const finish = valid => {if(settled)return;settled=true;window.clearTimeout(timer);URL.revokeObjectURL(url);resolve(valid);};
    const timer = window.setTimeout(()=>finish(false),15000);
    image.onload=()=>finish(image.naturalWidth>0 && image.naturalHeight>0);image.onerror=()=>finish(false);image.src=url;
  }));
  const release = () => { for(const url of urls.splice(0)) URL.revokeObjectURL(url); };
  const listen = (target,type,fn) => {target.addEventListener(type,fn);listeners.push(()=>target.removeEventListener(type,fn));};
  const updateVariant = () => {
    const option = selector.selectedOptions[0];
    const digital = form.querySelector('#df-digital');
    // The finish picker shows its own price (Casey, 21 Oct: one price); these are for a product without it.
    const priceOut = form.querySelector('#df-variant-price'), sizeOut = form.querySelector('#df-size-details');
    if (priceOut) priceOut.textContent = (option?.dataset.price || '') + (digital?.checked && digital.dataset.price ? ' + ' + digital.dataset.price + ' Digital Portrait' : '');
    if (sizeOut) sizeOut.textContent = describeFramedSize(option?.dataset.size, form.querySelector('[name="properties[Orientation]"]:checked')?.value);
    button.disabled = submitting || uncertain || completed || !option || option.disabled;
  };
  const updatePhotos = () => {
    release(); preview.replaceChildren();
    for(const input of form.querySelectorAll('[data-photo-input]')) {
      if(!photoInputNames(form).has(input.name)) continue;
      const file=input.files?.[0]; if(!file) continue;
      const url=URL.createObjectURL(file); urls.push(url);
      const figure=document.createElement('figure'), image=document.createElement('img'), caption=document.createElement('figcaption');
      image.src=url; image.alt='Selected reference photo'; caption.textContent=input.name === 'properties[_Photo 1]' ? 'Your favourite photo' : 'Reference photo ' + (Number(input.name.match(/\d+/)[0]) - 1);
      figure.append(image,caption);preview.append(figure);
    }
  };
  listen(form,'change',event=>{if(event.target.matches('[data-photo-input]')) updatePhotos();updateVariant();});
  const submit = async event => {
    event.preventDefault();
    // Enter in a text box while "Or order now" is folded away must not send anyone to checkout (Basic Guns, 21 Oct).
    if (button.closest?.('details:not([open])')) return;
    if(submitting || uncertain || completed) return;
    error.textContent='';checkCart.hidden=true;
    submitting=true;status.textContent='Preparing your photos…';
    try { await shrinkPhotoInputs(form, window); } catch { /* the checks below explain anything still wrong */ }
    submitting=false;status.textContent='';
    if(destroyed)return;
    const prepared=preparePortraitPayload(form,window.FormData,reference);
    if(prepared.error){error.textContent=prepared.error;return;}
    if(!prepared.variantId || selector.selectedOptions[0]?.disabled){error.textContent='Choose an available portrait size.';return;}
    const cartURL=form.dataset.cartUrl,addURL=form.dataset.cartAddUrl;
    if(!cartURL?.startsWith('/') || cartURL.startsWith('//') || !addURL?.startsWith('/') || addURL.startsWith('//')){error.textContent='Ordering is not configured yet.';return;}
    // Already a portrait of these pets in the cart? Ask before adding another (James's #1026 had three).
    submitting=true;
    try {
      const handle=(window.location.pathname.split('/products/')[1]||'').split(/[/?#]/)[0];
      const {response:cr,result:existing}=await requestJSON(cartURL+'.js',{headers:{Accept:'application/json'},cache:'no-store'});
      const twin=cr.ok?sameInCart(existing,handle,prepared.data):null;
      const who=twin&&(prepared.data.get('properties[Companion name]')||[1,2,3].map(n=>prepared.data.get('properties[Pet '+n+' name]')).filter(Boolean).join(' & '));
      if(twin&&!window.confirm('Just checking: you already have a portrait of '+who+' in your cart. Did you mean to add another one? Totally fine if you did, lots of people order one for family too.\n\nOK adds another. Cancel keeps just the one.')){submitting=false;error.textContent='No worries, you’ve still got the one in your cart.';checkCart.hidden=false;return;}
    } catch { /* can't read the cart: carry on as before */ }
    submitting=false;
    submitting=true;freeze(true);updateVariant();status.textContent='Checking your photos…';
    let added=false;
    try {
      const selectedFiles=prepared.files;
      const contentError=await validatePhotoContents(selectedFiles);
      if(contentError){error.textContent=contentError;return;}
      const readable=await Promise.all(selectedFiles.map(file=>Promise.resolve().then(()=>readablePhoto(file)).catch(()=>false)));
      // A picture this browser can't draw (a Samsung HEIC, say) still goes: Rover converts it (Casey, 1 Nov: "ingest anything").
      // Only a non-picture, or one over Shopify's 20 MB, is stopped.
      const stuck=selectedFiles.filter((file,i)=>!readable[i]);
      if(stuck.some(file=>isNotPicture(file))){error.textContent=photoFormatError();return;}
      if(selectedFiles.some(file=>file.size>CART_RAW_MAX_BYTES)){error.textContent=FREE_ERRORS.PHOTO_TOO_LARGE;return;} // Shopify's limit, for every file
      if(destroyed)return;
      status.textContent='Uploading your photos and adding your portrait…';
      // Raw photos can be up to 20 MB: give the upload 10 s a MB on top, at most 3 minutes (Big Guns, 1 Nov: a weak in-app link
      // otherwise hit "could not confirm" with the button locked).
      const sentBytes=selectedFiles.reduce((sum,file)=>sum+file.size,0);
      const {response,result}=await requestJSON(addURL+'.js',{method:'POST',headers:{Accept:'application/json'},body:prepared.data},Math.min(180000,requestTimeoutMs+10000*Math.ceil(sentBytes/1e6)));
      if(!response.ok) {
        if(response.status >= 500 || response.status === 408) throw new Error('Ambiguous server response');
        error.textContent=typeof result.description==='string'?result.description:'Your portrait could not be added. Please check your photos and try again.';
        return;
      }
      added=true;
      const {response:cartResponse,result:cart}=await requestJSON(cartURL+'.js',{headers:{Accept:'application/json'},cache:'no-store'});
      if(!cartResponse.ok) throw new Error('Cart verification failed');
      if(!verifyPortraitInCart(cart,{reference,variantId:prepared.variantId,photoKeys:prepared.photoKeys,origin:window.location.origin})) throw new Error('Photo persistence was not confirmed');
      // The high-resolution digital file add-on, linked to this portrait by its reference.
      const digital=form.querySelector('#df-digital');
      if(digital?.checked && digital.dataset.variant){
        status.textContent='Adding your Digital Portrait…';
        let ok=false;
        try{const {response:addOn}=await requestJSON(addURL+'.js',{method:'POST',headers:{Accept:'application/json','Content-Type':'application/json'},body:JSON.stringify({items:[{id:Number(digital.dataset.variant),quantity:1,properties:{'_For portrait':reference}}]})});ok=addOn.ok;}catch{ok=false;}
        if(!ok){completed=true;checkCart.hidden=false;error.textContent='Your portrait was added, but the Digital Portrait couldn\'t be. Check your cart to add it before paying.';return;}
      }
      completed=true;
      // Straight to checkout (one product, one size); the cart page stays reachable from the header link.
      const checkoutURL=form.dataset.checkoutUrl;
      if(!destroyed) window.location.assign(checkoutURL?.startsWith('/') && !checkoutURL.startsWith('//') ? checkoutURL : cartURL);
    } catch {
      // Do not resend an ambiguous upload: Shopify does not promise idempotency here.
      uncertain=true;checkCart.hidden=false;
      error.textContent=added?'Your portrait was added, but we could not verify every saved photo. Check your cart and photos before continuing.':'We could not confirm whether your portrait was added. Check your cart before trying again.';
    } finally {
      submitting=false;freeze(false);status.textContent='';if(!destroyed)updateVariant();
    }
  };
  listen(form,'submit',submit);listen(window,'pagehide',release);
  listen(window,'pageshow',event=>{
    if(!event.persisted)return;
    updatePhotos();
    if(completed){status.textContent='This portrait was added to your cart. Check your cart to continue.';checkCart.hidden=false;}
    updateVariant();
  });updateVariant();
  return {destroy(){destroyed=true;release();for(const off of listeners)off();}};
}
// See it before you pay (Casey, 21 Oct): the same photos and details as the cart, plus an email, sent straight to Rover
// (no checkout). The form's own checks run first (required fields, photos), exactly as for Add to cart.
const FREE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FREE_ERRORS = {EMAIL_REQUIRED:'Please enter your email address.',PHOTO_UNREADABLE:'A photo could not be opened. Please choose another image.',PHOTO_TOO_LARGE:'A photo is too large. Please choose a smaller one.',PHOTO_REQUIRED:'Choose your favourite photo before continuing.',TOO_MANY_TRIES:'You\'ve sent a few already. Please try again in an hour.',FREE_PREVIEW_BUSY:'We\'re a little busy right now. Please try again in a minute.',BODY_TOO_LARGE:'Your photos are too large together. Please choose smaller ones.'};
export function prepareFreePreviewPayload(form, FormDataType, {email, firstName, website, productId}) {
  if (!FREE_EMAIL.test(String(email || '').trim())) return {error: FREE_ERRORS.EMAIL_REQUIRED};
  const prepared = preparePortraitPayload(form, FormDataType, 'free-preview');
  if (prepared.error) return prepared;
  const data = prepared.data;
  // The finish they chose (framed, a canvas, or the digital file): Rover makes and prices the preview's checkout from it.
  const finish = form.querySelector?.('#df-variant')?.selectedOptions?.[0]?.dataset?.finish;
  for (const key of ['id', 'quantity', 'form_type', 'utf8', 'properties[_Dear Friend reference]', 'properties[_Photo count]', 'properties[Gift]']) data.delete(key);
  if (finish) data.set('properties[_Finish]', finish);
  // A real pick (Captain, 27 Oct): the Prices or home page link ticked it (?size= / ?finish=). Without it the hidden picker's
  // default (Large framed) is no pick, and the preview page opens on Casey's default instead.
  const search = String((typeof window !== 'undefined' && window.location && window.location.search) || '');
  if (finish) data.set('properties[_Picked]', /[?&](size|finish)=/i.test(search) ? 'yes' : 'no'); // "no": the default, whatever it is (Canada's is canvas)
  data.set('email', String(email).trim()); data.set('first_name', String(firstName || '').trim());
  data.set('website', String(website || '')); data.set('product_id', String(productId || ''));
  return {data, files: prepared.files};
}
// The first thing the free preview still needs, in page order, with the words the checks below would use: a required
// field, a photo, then the email. Null when it's ready to send.
export function firstMissing(form, email) {
  const names = photoInputNames(form);
  const photos = [...form.querySelectorAll('[data-photo-input]')]
    .filter(el => !el.disabled && names.has(el.name) && !el.files?.length && (el.name === 'properties[_Photo 1]' || el.dataset.photoRequired))
    .map(el => ({el, message: el.dataset.photoRequired || 'Choose your favourite photo before continuing.'}));
  const fields = [...form.querySelectorAll(':invalid')].filter(el => !el.matches('fieldset, form')).map(el => ({el, message: el.dataset.missing || el.validationMessage})); // a field can name itself ("Choose male or female for pet 2.")
  const first = [...fields, ...photos].sort((a, b) => (a.el.compareDocumentPosition(b.el) & 4 ? -1 : 1))[0];
  if (first) return first;
  if (!FREE_EMAIL.test(String(email || '').trim())) return {el: form.ownerDocument.getElementById('df-free-email'), message: FREE_ERRORS.EMAIL_REQUIRED};
  return null;
}
// "We're on it" (Casey, 24 Oct): the buyer by first name, and the pets' portrait by name (never the people in it, so a gift
// reads right): "Bolt and Ziggy's portrait is with our artists now."
export function onItWords(form, firstName) {
  const names = [...form.querySelectorAll('[name^="properties[Pet "][name$=" name]"], [name="properties[Companion name]"]')]
    .filter(f => !f.disabled && String(f.value || '').trim()).map(f => String(f.value).trim());
  const list = names.length < 2 ? names.join('') : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  const first = String(firstName || '').trim();
  return { heading: first ? `We\u2019re on it, ${first}!` : 'We\u2019re on it!', line: list ? `${list}\u2019s portrait is with our artists now.` : 'Your portrait is with our artists now.' };
}
// Their photos as polaroids, in the order they added them (people first, then each pet's).
export function showOnIt(done, form, firstName, window) {
  const w = onItWords(form, firstName), pics = done.querySelector('[data-onit-pics]');
  const set = (sel, t) => { const el = done.querySelector(sel); if (el) el.textContent = t; };
  set('[data-onit-heading]', w.heading); set('[data-onit-line]', w.line);
  if (!pics) return;
  const files = [...form.querySelectorAll('[data-photo-input]')].filter(i => !i.disabled).flatMap(i => [...(i.files || [])]).slice(0, 12);
  pics.textContent = ''; pics.dataset.n = String(files.length);
  for (const f of files) { const img = window.document.createElement('img'); img.alt = ''; img.onload = () => window.URL.revokeObjectURL(img.src); try { img.src = window.URL.createObjectURL(f); } catch { continue; } pics.appendChild(img); }
}
// A free preview really started (Captain for Casey, 25 Oct: Meta "Lead"): a Shopify customer event the store's custom pixel
// turns into Meta's Lead. No personal data: only the product and a one-off id, which Meta uses so it never counts twice.
export function leadEvent(window, productId) {
  try {
    const id = window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    return window.Shopify?.analytics?.publish?.('df_free_preview', {event_id: `df-fp-${id}`, product_id: String(productId || '')});
  } catch { /* tracking never gets in the way of their request */ }
}
// Before leaving for their preview page (Basic Guns, 31 Oct): give the Lead time to reach the pixel. publish() only confirms
// dispatch, so wait for it plus half a second, never more than a second in all; the progress page hides the wait.
export function leadSettled(window, sent, {extra = 500, cap = 1000} = {}) {
  const sleep = ms => new Promise(r => window.setTimeout(r, ms));
  return Promise.race([Promise.resolve(sent).catch(() => {}).then(() => sleep(extra)), sleep(cap)]);
}
// The form's steps for the weekly numbers (Anna's batch 2, Builder, 26 Oct): plain-text beacons to Rover with a random id
// for the visit, which also rides on the request as _Session so the steps join it. Only fixed words go: the style's name,
// "2 pets + 1 person", a code in capitals. Never anything typed (names, notes, file names, the email). Never in the way.
const STEP_URL = 'https://portraits.dearfriend.pet/api/form-event';
export function whoWords(form) {
  const pets = Number(form.querySelector('[name="df-pets"]:checked')?.value) || 1;
  const people = form.querySelector('[name="df-people"]') ? Number(form.querySelector('[name="df-people"]:checked')?.value) || 1 : 0;
  return `${pets} ${pets === 1 ? 'pet' : 'pets'}${people ? ` + ${people} ${people === 1 ? 'person' : 'people'}` : ''}`;
}
export function formSteps(window, form) {
  const noop = () => {};
  try {
    let sid = '';
    try { sid = window.sessionStorage.getItem('df_sid') || ''; } catch { /* private mode: a new id for this page */ }
    if (!/^[A-Za-z0-9_-]{8,64}$/.test(sid)) {
      sid = String(window.crypto?.randomUUID?.() || `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`).replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
      try { window.sessionStorage.setItem('df_sid', sid); } catch { /* still fine for this page */ }
    }
    const product = (String(window.location?.pathname || '').match(/\/products\/([a-z0-9-]+)/) || [])[1]?.slice(0, 40) || '';
    const step = (name, extra = {}) => {
      try { window.navigator.sendBeacon(STEP_URL, new window.Blob([JSON.stringify({sid, step: name, product, ...extra})], {type: 'text/plain'})); } catch { /* never in the way */ }
    };
    let session = form.querySelector('input[name="properties[_Session]"]');
    if (!session) { session = form.ownerDocument.createElement('input'); session.type = 'hidden'; session.name = 'properties[_Session]'; form.appendChild(session); }
    session.value = sid;
    // Their device's time zone, for the time their free preview is held until in our emails (Casey, 26 Oct).
    let zone = ''; try { zone = window.Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { /* the email says about how many hours */ }
    if (zone) {
      let z = form.querySelector('input[name="properties[_Zone]"]');
      if (!z) { z = form.ownerDocument.createElement('input'); z.type = 'hidden'; z.name = 'properties[_Zone]'; form.appendChild(z); }
      z.value = zone;
    }
    step('open');
    form.addEventListener('change', e => {
      const t = e.target;
      if (t?.type === 'radio' && t.name === 'properties[Style]') step('style', {detail: t.value});
      else if (t?.name === 'df-cstyle') step('style', {detail: t.value || 'Custom'});
      else if (t?.name === 'df-pets' || t?.name === 'df-people') step('who', {detail: whoWords(form)});
    });
    return step;
  } catch { return noop; }
}
const CODE_OF = Object.fromEntries(Object.entries(FREE_ERRORS).map(([k, v]) => [v, k]));
const codeOf = v => String(v || '').toUpperCase().replace(/[^A-Z0-9_]/g, '').slice(0, 40) || 'UNKNOWN';
export function mountFreePreview({document, window, fetch, requestTimeoutMs = 180000}) {
  const form = document.getElementById('DearFriendProductForm'), box = document.getElementById('df-free');
  if (!form || !box || box.hidden) return null;
  const $ = id => document.getElementById(id);
  const open = $('df-free-open'), fields = $('df-free-fields'), send = $('df-free-send'), error = $('df-free-error'), status = $('df-free-status'), done = $('df-free-done');
  let busy = false;
  const step = formSteps(window, form);
  let emailSent = false;
  $('df-free-email')?.addEventListener('change', e => { if (!emailSent && FREE_EMAIL.test(String(e.target.value || '').trim())) { emailSent = true; step('email'); } });
  open.addEventListener('click', () => { fields.hidden = false; open.hidden = true; $('df-free-email').focus(); });
  fields.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); send.click(); } }); // never Add to cart by accident
  // Item 10 (Casey, Sat): the button breathes slowly until everything it needs is in, then quicker and brighter. Pressed
  // early, it takes them to the first thing missing, rings it in gold and says what it is just under it.
  let note = null;
  const ready = () => {
    const gap = firstMissing(form, $('df-free-email').value);
    send.classList.toggle('is-ready', !gap);
    if (note && (!gap || !note.dfFor.contains(gap.el))) { note.remove(); note = null; }
    else if (note) note.textContent = gap.message; // the same field, maybe a different problem now
    return gap;
  };
  const point = ({el, message}) => {
    const spot = el.closest('.df-box, .upload-card, .field, [role="radiogroup"]') || el;
    spot.scrollIntoView({behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center'});
    spot.classList.remove('df-attn'); void spot.offsetWidth; spot.classList.add('df-attn');
    window.setTimeout(() => spot.classList.remove('df-attn'), 2600);
    if (!['file', 'radio', 'checkbox'].includes(el.type)) el.focus({preventScroll: true});
    note?.remove(); note = document.createElement('p'); note.className = 'error df-attn-msg'; note.setAttribute('role', 'alert');
    note.textContent = message; note.dfFor = spot; spot.after(note);
  };
  for (const type of ['input', 'change']) form.addEventListener(type, ready);
  ready();
  send.addEventListener('click', async () => {
    if (busy) return;
    error.textContent = '';
    const gap = ready();
    if (gap) { point(gap); step('error', {detail: gap.el.id === 'df-free-email' ? 'EMAIL_REQUIRED' : gap.el.matches?.('[data-photo-input]') ? 'PHOTO_REQUIRED' : 'FIELD_REQUIRED'}); return; }
    if (!form.reportValidity()) { step('error', {detail: 'FIELD_REQUIRED'}); return; }
    busy = true; send.disabled = true; status.textContent = 'Sending your photos…';
    if (!emailSent) { emailSent = true; step('email'); } // Enter in the email box sends without a change event
    step('submit'); step('upload_start');
    const started = Date.now();
    let timedOut = false;
    try {
      // Up to three sends (Leanne, 24 Oct): if the upload is cut off or too big, shrink every photo harder and send again by
      // itself, so a weak connection or an in-app browser still gets through without the customer doing anything.
      let response, result = {}, lastTotal = 0;
      for (let attempt = 0; attempt < 3; attempt++) {
        if (attempt) status.textContent = 'Still sending your photos…';
        try { await shrinkPhotoInputs(form, window, attempt); } catch { /* the checks below explain anything still wrong */ }
        const prepared = prepareFreePreviewPayload(form, window.FormData, {email: $('df-free-email').value, firstName: $('df-free-name').value, website: $('df-free-website').value, productId: box.dataset.productId});
        if (prepared.error) { error.textContent = prepared.error; step('upload_failed', {detail: CODE_OF[prepared.error] || 'PHOTO_CHECK'}); return; }
        const contentError = await validatePhotoContents(prepared.files);
        if (contentError) { error.textContent = contentError; step('upload_failed', {detail: CODE_OF[contentError] || 'PHOTO_CHECK'}); return; }
        // Raw photos can't be shrunk: if they still add up to more than Rover's form takes, say so now instead of sending it
        // three times and blaming the connection (Basic Guns, 1 Nov). A retry that made nothing smaller isn't worth sending.
        const total = prepared.files.reduce((sum, file) => sum + file.size, 0);
        if (total > FORM_MAX_BYTES || (attempt && total >= lastTotal && response?.status === 413)) { error.textContent = FREE_ERRORS.BODY_TOO_LARGE; step('upload_failed', {detail: 'BODY_TOO_LARGE'}); return; }
        lastTotal = total;
        const controller = new window.AbortController(), timer = window.setTimeout(() => controller.abort(), requestTimeoutMs);
        response = null; result = {};
        try { response = await fetch(box.dataset.freeUrl, {method: 'POST', body: prepared.data, signal: controller.signal}); result = await response.json().catch(() => ({})); }
        catch { response = null; timedOut = controller.signal.aborted; }
        finally { window.clearTimeout(timer); }
        if (response && !(response.status === 413 || result.error === 'BODY_TOO_LARGE' || (response.status >= 502 && !result.error))) break; // Rover's own refusals (busy, off) are final
      }
      if (!response) { error.textContent = 'Your photos didn\'t finish sending. Please check your connection and press the button again.'; step('upload_failed', {detail: timedOut ? 'TIMEOUT' : 'NO_CONNECTION'}); return; }
      if (!response.ok) {
        error.textContent = FREE_ERRORS[result.error] || 'Sorry, that didn\'t go through. Please try again, or email orders@dearfriend.pet.';
        const cut = response.status === 413 || result.error === 'BODY_TOO_LARGE' || (response.status >= 502 && !result.error);
        if (!cut) step('upload_done', {ms: Date.now() - started}); // the photos arrived; Rover said no (busy, too many tries)
        step(cut ? 'upload_failed' : 'error', {detail: codeOf(result.error || `HTTP_${response.status}`)});
        return;
      }
      step('upload_done', {ms: Date.now() - started});
      const lead = result.started ? leadEvent(window, box.dataset.productId) : null;
      // Instant free portrait preview (Casey, 31 Oct): Rover answers with their own preview page, which shows the progress until
      // it's ready; go straight there. Only https on Rover's own address. Without a page (instant off), today's "We're on it".
      if (result.page && /^https:\/\/portraits\.dearfriend\.pet\//.test(String(result.page))) { status.textContent = 'Opening your preview…'; if (result.started) await leadSettled(window, lead); window.location.assign(String(result.page)); return; }
      fields.hidden = true;
      // No page but an artist line (instant on, Builder 31 Oct: e.g. their address is at its limit): Rover's words, as text.
      if (result.artist) { const art = $('df-free-artist'); art.querySelector('p').textContent = String(result.artist); art.hidden = false; }
      else if (result.full) $('df-free-full').hidden = false; // the shop's daily cap, or this place's weekly limit
      else { try { showOnIt(done, form, $('df-free-name').value, window); } catch { /* the plain box still shows: it was sent */ } done.hidden = false; done.scrollIntoView({block: 'center', behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'}); }
    } catch { error.textContent = 'Sorry, that didn\'t go through. Please try again, or email orders@dearfriend.pet.'; step('error', {detail: 'UNKNOWN'}); }
    finally { busy = false; send.disabled = false; status.textContent = ''; }
  });
  return {};
}
if(typeof document!=='undefined') mountPurchase({document,window,URL,fetch:window.fetch.bind(window)});
if(typeof document!=='undefined') mountFreePreview({document,window,fetch:window.fetch.bind(window)});
